import { CONFIG } from "../../../../config";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";

const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

// Development logging utility
const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
  if (isDevelopment) {
    console.log(message, data);
  }
};

/**
 * Custom hook for exporting special status analytics to XLSX
 * 
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Object} Mutation object with download function
 */
export const useExportSpecialStatusAnalytics = (options = {}) => {
  const {
    onSuccess = () => { },
    onError = () => { },
  } = options;

  // Download function that creates blob and triggers download
  const downloadFile = useCallback((blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      devLog('[useExportSpecialStatusAnalytics] File downloaded successfully:', filename);
    } catch (error) {
      console.error('[useExportSpecialStatusAnalytics] Download failed:', error);
      throw new Error('Failed to download file');
    }
  }, []);

  // Export function
  const exportSpecialStatusAnalytics = useCallback(async (params = {}) => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useExportSpecialStatusAnalytics] Making export request with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/exports/special-status-analytics`,
        {
          params: cleanParams,
          responseType: 'blob', // Important for file downloads
          timeout: 60000, // 60 second timeout for exports
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
      );

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'special-status-analytics-export.xlsx';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      downloadFile(blob, filename);

      devLog('[useExportSpecialStatusAnalytics] Export completed successfully');

      return { success: true, filename };
    } catch (error) {
      // Enhanced error handling
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error exporting special status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Export timeout - file may be too large';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  }, [downloadFile]);
}
/**
 * Custom hook for exporting special status analytics to XLSX with date range support
 * 
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {Object} Mutation object with download function
 */
export const useExportSpecialStatusAnalyticsRange = (options = {}) => {
  const {
    onSuccess = () => { },
    onError = () => { },
  } = options;

  // Download function that creates blob and triggers download
  const downloadFile = useCallback((blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      devLog('[useExportSpecialStatusAnalyticsRange] File downloaded successfully:', filename);
    } catch (error) {
      console.error('[useExportSpecialStatusAnalyticsRange] Download failed:', error);
      throw new Error('Failed to download file');
    }
  }, []);

  // Export function
  const exportSpecialStatusAnalyticsRange = useCallback(async (params = {}) => {
    try {
      // Filter out undefined/null values from params
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          value !== undefined && value !== null && value !== ''
        )
      );

      devLog('[useExportSpecialStatusAnalyticsRange] Making export request with params:', cleanParams);

      const response = await axios.get(
        `${API_HOST_CRM}/analytics/exports/special-status-analytics`,
        {
          params: cleanParams,
          responseType: 'blob', // Important for file downloads
          timeout: 60000, // 60 second timeout for exports
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
      );

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'special-status-analytics-export.xlsx';

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      downloadFile(blob, filename);

      devLog('[useExportSpecialStatusAnalyticsRange] Export completed successfully');

      return { success: true, filename };
    } catch (error) {
      // Enhanced error handling
      const isNetworkError = !error.response && error.code === 'ENOTFOUND';
      const isTimeoutError = error.code === 'ECONNABORTED';

      let errorMessage = 'Error exporting special status analytics';

      if (isNetworkError) {
        errorMessage += ': Network connection failed';
      } else if (isTimeoutError) {
        errorMessage += ': Export timeout - file may be too large';
      } else if (error.response) {
        errorMessage += `: Server responded with ${error.response.status}`;
      }

      console.error(errorMessage, {
        originalError: error,
        params,
        timestamp: new Date().toISOString()
      });

      throw new Error(errorMessage);
    }
  }, [downloadFile]);

  return useMutation({
    mutationFn: exportSpecialStatusAnalyticsRange,
    onSuccess: (data, variables) => {
      devLog('[useExportSpecialStatusAnalyticsRange] Export mutation succeeded:', data);
      onSuccess(data, variables);
    },
    onError: (error, variables) => {
      console.error("Error in useExportSpecialStatusAnalyticsRange mutation:", {
        error: error.message,
        params: variables,
        timestamp: new Date().toISOString()
      });
      onError(error, variables);
    },
  });
};
